import * as React from "react";
import * as TooltipPrimitive from "radix-ui/tooltip";
import { cn } from "@/lib/utils";

// One TooltipProvider is mounted in app/Providers.tsx; nothing else needs to mount one.

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

/** Strong-text colour as the fill: dark on light in light mode, and it inverts in dark. */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        "z-50 max-w-[260px] origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-tooltip px-2 py-[5px] text-[11.5px] leading-[1.4] font-medium text-tooltip-foreground fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="fill-tooltip" width={10} height={5} />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = "TooltipContent";

interface TooltipProps {
  /** Nothing renders, not even an empty bubble, when the title is empty. */
  title: React.ReactNode;
  children: React.ReactElement;
  side?: React.ComponentProps<typeof TooltipPrimitive.Content>["side"];
  align?: React.ComponentProps<typeof TooltipPrimitive.Content>["align"];
  className?: string;
}

/**
 * The shorthand most call sites want, shaped like MUI's `<Tooltip title>` around one
 * child; the Root / Trigger / Content parts are there for anything more.
 *
 * The child must take a ref and pointer events. A DISABLED button takes no pointer
 * events, so wrap it in a <span> -- the same rule MUI had.
 */
const Tooltip = ({ title, children, side, align, className }: TooltipProps) => {
  if (title === null || title === undefined || title === false || title === "") return children;
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align} className={className}>
        {title}
      </TooltipContent>
    </TooltipRoot>
  );
};

export { Tooltip, TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider };
